import { useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const MIN_YEAR = 2020, MAX_YEAR = 2100;
const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const key = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;
const shift = (ym, n) => { const [y, m] = ym.split("-").map(Number); const d = new Date(y, m - 1 + n, 1); return key(d.getFullYear(), d.getMonth()); };
const inRange = (ym) => ym >= `${MIN_YEAR}-01` && ym <= `${MAX_YEAR}-12`;
export const monthFull = (ym) => { if (!ym) return "—"; const [y, m] = ym.split("-").map(Number); return `${MONTHS[m - 1]}/${y}`; };

export const MonthPanel = ({ value, onChange, marked = [], testId = "month-picker" }) => {
  const now = key(new Date().getFullYear(), new Date().getMonth());
  const [year, setYear] = useState(Number((value || now).slice(0, 4)));
  const [mode, setMode] = useState("months");
  const [decade, setDecade] = useState(MIN_YEAR + Math.floor((Number((value || now).slice(0, 4)) - MIN_YEAR) / 12) * 12);
  const pick = (ym) => { if (!inRange(ym)) return; onChange(ym); setYear(Number(ym.slice(0, 4))); setMode("months"); };
  const presets = [
    { label: "Este mês", ym: now }, { label: "Mês passado", ym: shift(now, -1) },
    ...[2, 3, 4, 5, 6].map((n) => ({ label: monthFull(shift(now, -n)), ym: shift(now, -n) })),
    { label: "Mesmo mês, ano passado", ym: shift(now, -12) }, { label: `Início (Jan/${MIN_YEAR})`, ym: `${MIN_YEAR}-01` },
  ];
  const years = Array.from({ length: 12 }, (_, i) => decade + i).filter((y) => y >= MIN_YEAR && y <= MAX_YEAR);

  return (
        <div className="flex">
          <div className="hidden sm:flex flex-col border-r border-border p-2 w-44 max-h-[320px] overflow-y-auto">
            {presets.filter((p) => inRange(p.ym)).map((p) => (
              <button key={p.label} type="button" onClick={() => pick(p.ym)} data-testid={`${testId}-preset-${p.ym}`}
                className={`text-left text-sm px-3 py-1.5 rounded-md transition-colors ${p.ym === value ? "bg-primary/15 text-primary font-medium" : "hover:bg-secondary text-foreground/90"}`}>{p.label}</button>
            ))}
          </div>
          <div className="p-3 w-[264px]">
            <div className="flex items-center justify-between mb-2">
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" data-testid={`${testId}-prev`} onClick={() => (mode === "months" ? setYear((y) => Math.max(MIN_YEAR, y - 1)) : setDecade((d) => Math.max(MIN_YEAR, d - 12)))}><ChevronLeft className="w-4 h-4" /></Button>
              <button type="button" className="font-display font-semibold text-sm px-3 py-1 rounded-md hover:bg-secondary num" data-testid={`${testId}-year`} onClick={() => { setMode(mode === "months" ? "years" : "months"); setDecade(MIN_YEAR + Math.floor((year - MIN_YEAR) / 12) * 12); }}>
                {mode === "months" ? year : `${years[0]} – ${years[years.length - 1]}`}
              </button>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" data-testid={`${testId}-next`} onClick={() => (mode === "months" ? setYear((y) => Math.min(MAX_YEAR, y + 1)) : setDecade((d) => Math.min(MAX_YEAR - 11, d + 12)))}><ChevronRight className="w-4 h-4" /></Button>
            </div>
            {mode === "months" ? (
              <div className="grid grid-cols-3 gap-1.5">
                {SHORT.map((s, i) => {
                  const ym = key(year, i), sel = ym === value, has = marked.includes(ym);
                  return (
                    <button key={s} type="button" onClick={() => pick(ym)} disabled={!inRange(ym)} data-testid={`${testId}-month-${ym}`}
                      className={`relative h-10 rounded-lg text-sm transition-colors ${sel ? "bg-primary text-primary-foreground font-semibold" : ym === now ? "border border-primary/60 hover:bg-secondary" : "hover:bg-secondary"} disabled:opacity-30`}>
                      {s}{has && !sel && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-1.5">
                {years.map((y) => (
                  <button key={y} type="button" onClick={() => { setYear(y); setMode("months"); }} data-testid={`${testId}-yearopt-${y}`}
                    className={`h-10 rounded-lg text-sm num transition-colors ${y === year ? "bg-primary text-primary-foreground font-semibold" : "hover:bg-secondary"}`}>{y}</button>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-3">Período disponível: Jan/{MIN_YEAR} a Dez/{MAX_YEAR}. <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 align-middle mx-1" />meses com DRE salvo.</p>
          </div>
        </div>
  );
};

export const MonthPicker = ({ value, onChange, marked = [], testId = "month-picker", className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testId} className={`justify-between gap-2 font-normal min-w-[210px] ${className}`}>
          <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-primary" /><span className="capitalize">{monthFull(value)}</span></span>
          <ChevronDown className="w-4 h-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0 w-auto" data-testid={`${testId}-popover`}>
        {open && <MonthPanel value={value} onChange={(ym) => { onChange(ym); setOpen(false); }} marked={marked} testId={testId} />}
      </PopoverContent>
    </Popover>
  );
};
